import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { ReportDailyActivityDto } from "@report/data/dto/report-daily-activity.dto.ts";
import { ReportDailyActivityDtoSchema } from "@report/data/dto/report-daily-activity.dto.ts";
import { Report } from "@report/domain/models/report.model";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

@injectable()
export class ReportDailyActivityMapper {
    map(
        reportGpsDto: ReportDailyActivityDto,
    ): Result<Report[], ModelError<typeof Report>> {
        const reportGpsDtoParsed =
            ReportDailyActivityDtoSchema.safeParse(reportGpsDto);

        if (!reportGpsDtoParsed.success) {
            console.error(reportGpsDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const reportsResults = Object.values(reportGpsDto.data).map(
            (report) => {
                const station = report.station;
                const rows = report.data.rows ?? {};
                const totals = report.data.totals;
                const reportPagination = report.pagination;

                const reportMapped = Object.entries(rows).map(
                    ([date, values]) => ({
                        date,
                        movingTime: values.moving_time,
                        stoppedTime: values.stopped_time,
                        workedTime: values.worked_time,
                        traveledKilometers: values.traveled_kilometers,
                    }),
                );

                const totalRow = {
                    date: "",
                    movingTime: totals.moving_time,
                    stoppedTime: totals.stopped_time,
                    workedTime: totals.worked_time,
                    traveledKilometers: totals.traveled_kilometers,
                };

                reportMapped.push(totalRow);

                return Report.createWithRecovery({
                    stationReportSummary: {
                        icon: station.icon,
                        stationId: station.id,
                        stationName: `${station.name} (${station.technical_name})`,
                    },
                    items: reportMapped,
                    pagination: {
                        count: reportPagination.count,
                        current: reportPagination.current,
                        pages: reportPagination.pages,
                        perPage: reportPagination.per_page,
                    },
                });
            },
        );

        if (reportsResults.some((result) => result.isErr())) {
            return err(new NonRecuperableModelError());
        }

        return ok(
            reportsResults
                .map((result) => result.unwrapOr(null))
                .filter(Boolean),
        );
    }
}
